// Custom element for bundle form with sticky bar
class BundleForm extends HTMLElement {
	constructor() {
		super();
		this.targetSelector = '.biscuits-bundle-volume-discounts__container';
		this.targetSelectorTotal = '.biscuits-bundle-volume-discounts';
		this.stickyBar = null;
		this.priceContainer = null;
		this.buyButton = null;
		this.observer = null;
		this.footerObserver = null;
		this.bundleForm = null;
		this.target = null;
		this.isVisible = false;
		this.isFooterVisible = false;
	}

	connectedCallback() {
		// Wait for the target element to be injected by the Biscuits Bundle app
		this.waitForTarget();

		// Setup event listeners for Biscuits Bundle events (called only once)
		this.setupBiscuitsEvents();

		// Setup footer observer to hide bar when footer is visible
		this.setupFooterObserver();
	}

	waitForTarget() {
		const checkInterval = setInterval(() => {
			const target = document.querySelector(this.targetSelector);
			const totalQuantity = document.querySelector(this.targetSelectorTotal);

			if (target) {
				clearInterval(checkInterval);
				this.setupStickyBar(target, totalQuantity);
			}
		}, 100);

		// Stop checking after 5 seconds
		setTimeout(() => clearInterval(checkInterval), 5000);
	}

	setupBiscuitsEvents() {
		// Listen to step updates to refresh pricing and clone
		document.querySelectorAll('.biscuits-bundle-step').forEach((step) => {
			step.addEventListener('biscuits--step-update', (event) => {
				// Update pricing immediately
				this.updatePricing();

				// Re-clone the div after a small delay to let Biscuits Bundle update the DOM
				if (this.isVisible && this.target) {
					setTimeout(() => {
						this.updateStickyBar(this.target, this.totalQuantity);
					}, 50);
				}
			});
		});

		// Also listen to the form ready event to get initial pricing
		document.addEventListener('BiscuitsBundleForm:ready', (event) => {
			this.bundleForm = document.querySelector('biscuits-bundle-form');
			this.updatePricing();
		});

		this.setupClickListeners();
	}

	setupStickyBar(target, totalQuantity) {
		// Save target reference
		this.target = target;
		this.totalQuantity = totalQuantity;

		// Create sticky bar
		this.stickyBar = document.createElement('div');
		this.stickyBar.className = 'bundle-sticky-bar';
		this.stickyBar.setAttribute('data-biscuits-style', 'thumbnails');
		this.stickyBar.setAttribute('data-total', totalQuantity.dataset.total || 0);

		// Create price container
		this.priceContainer = document.createElement('div');
		this.priceContainer.className = 'bundle-sticky-bar__pricing';

		// Create buy button
		this.createBuyButton();

		// Append to body
		document.body.appendChild(this.stickyBar);

		// Setup Intersection Observer
		this.observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						// Target is visible, hide sticky bar
						this.isVisible = false;
						this.updateStickyBarVisibility();
					} else {
						// Target is not visible, show sticky bar with cloned content
						this.isVisible = true;
						this.updateStickyBar(target, totalQuantity);
						this.updateStickyBarVisibility();
					}
				});
			},
			{
				threshold: 0,
				rootMargin: '0px',
			},
		);

		this.observer.observe(target);

		// Synchronize styles initially
		this.syncStyles();
	}

	syncStyles() {
		if (!this.stickyBar) return;

		const computedStyle = getComputedStyle(this);
		const variables = [
			'--palier-1',
			'--palier-2',
			'--palier-3',
			'--palier-4',
			'--palier-5',
			'--palier-6',
		];

		variables.forEach((variable) => {
			const value = computedStyle.getPropertyValue(variable).trim();
			if (value) {
				this.stickyBar.style.setProperty(variable, value);
			}
		});
	}

	setupFooterObserver() {
		const footer = document.querySelector('.footer__aside');

		if (!footer) return;

		this.footerObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					this.isFooterVisible = entry.isIntersecting;
					this.updateStickyBarVisibility();
				});
			},
			{
				threshold: 0,
				rootMargin: '0px',
			},
		);

		this.footerObserver.observe(footer);
	}

	updateStickyBarVisibility() {
		if (!this.stickyBar) return;

		// Hide if target is visible OR if footer is visible
		if (!this.isVisible || this.isFooterVisible) {
			this.stickyBar.style.display = 'none';
		} else {
			this.stickyBar.style.display = 'flex';
		}
	}

	createBuyButton() {
		// Create a simple buy button
		this.buyButton = document.createElement('button');
		this.buyButton.className = 'bundle-sticky-bar__buy-button button';
		this.buyButton.textContent = 'Ajouter au panier';
		this.buyButton.type = 'button';

		// Add click event to trigger original buy button
		this.buyButton.addEventListener('click', () => {
			const originalButton = document.querySelector('.product .buy-buttons button[type="submit"]');
			if (originalButton) {
				originalButton.click();
			}
		});
	}

	updateStickyBar(target, totalQuantity) {
		// Clone the target element
		const clone = target.cloneNode(true);

		// Clear sticky bar and add clone + pricing + buy button
		this.stickyBar.innerHTML = '';
		this.stickyBar.appendChild(clone);
		this.stickyBar.appendChild(this.priceContainer);
		this.stickyBar.setAttribute('data-total', totalQuantity.dataset.total || 0);

		// Add buy button if available
		if (this.buyButton) {
			this.stickyBar.appendChild(this.buyButton);
		}

		// Update pricing
		this.updatePricing();

		// Sync styles
		this.syncStyles();
	}

	setupClickListeners() {
		// Delegated click listener for biscuits-bundle-volume-discount__quantity
		document.addEventListener('click', (event) => {
			const target = event.target.closest('.biscuits-bundle-volume-discount__card');
			if (!target) return;

			// Find the parent tier and look for the variant id in the image container
			const tier = target.closest('.biscuits-bundle-volume-discount-tier');
			if (!tier) return;

			const imageContainer = tier.querySelector('.biscuits-bundle-item__image');
			const variantId = imageContainer?.getAttribute('data-id');

			if (!variantId) return;

			// Find the element with the variant ID and click the minus button
			const productEl = document.querySelector(
				`[data-biscuits-selected-variant-id="${variantId}"]`,
			);
			if (productEl) {
				const minusBtn = productEl.querySelector('.biscuits-bundle-quantity__button--minus');
				if (minusBtn) {
					minusBtn.click();
				}
			}
		});
	}

	updatePricing() {
		if (!this.priceContainer) return;

		const totalPrice = this.bundleForm?.formTotalPrice || 0;
		const compareAtPrice = this.bundleForm?.formCompareAtPrice || 0;
		let savingsPercentage = this.bundleForm?.formSavingsPercentage || 0;

		// Try to calculate percentage manually if not provided
		if (
			(!savingsPercentage || savingsPercentage === 0) &&
			compareAtPrice > totalPrice &&
			totalPrice > 0
		) {
			savingsPercentage = ((compareAtPrice - totalPrice) / compareAtPrice) * 100;
		}

		// Logic to get the discount value from palier or fallback to calculated percentage
		const totalLevel = this.stickyBar?.getAttribute('data-total');
		let finalDiscountValue = Math.round(savingsPercentage);
		let discountText = '';

		if (totalLevel && totalLevel !== '0') {
			const palierValue = getComputedStyle(this)
				.getPropertyValue(`--palier-${totalLevel}`)
				.trim()
				.replace(/"/g, '');
			if (palierValue) {
				discountText = `-${palierValue}`;
				// Clean numeric value for the hidden input (e.g., "20%" -> 20)
				const numericValue = parseInt(palierValue);
				if (!isNaN(numericValue)) {
					finalDiscountValue = numericValue;
				}
			}
		}

		
		const originalBundleForm = document.querySelector(
			'.product .product-info__block-item .shopify-product-form',
		);

		if (originalBundleForm) {
			const updateOrAddInput = (name, value) => {
				let input = originalBundleForm.querySelector(`input[name="${name}"]`);
				if (!input) {
					input = document.createElement('input');
					input.type = 'hidden';
					input.name = name;
					originalBundleForm.prepend(input);
				}
				input.value = value;
			};
			updateOrAddInput('properties[_compareAtPrice]', compareAtPrice);
			updateOrAddInput('properties[_discount]', finalDiscountValue);
		}

		let priceHTML = '';

		// Show discount badge
		if (discountText) {
			priceHTML += `<span class="bundle-sticky-bar__badge">${discountText}</span>`;
		}

		// Show current price (always show, even if 0)
		const formattedPrice = this.formatPrice(totalPrice);
		priceHTML += `<span class="bundle-sticky-bar__price">${formattedPrice}</span>`;

		// Show compare at price if available and greater than total price
		if (compareAtPrice && compareAtPrice > totalPrice) {
			const formattedComparePrice = this.formatPrice(compareAtPrice);
			priceHTML += `<span class="bundle-sticky-bar__compare-price">${formattedComparePrice}</span>`;
		}

		this.priceContainer.innerHTML = priceHTML;

		// Disable buy button if price is 0
		if (this.buyButton) {
			this.buyButton.disabled = totalPrice === 0;
		}
	}

	formatPrice(cents) {
		// Format price in euros (adjust based on your currency)
		const euros = cents / 100;
		return euros.toLocaleString('fr-FR', {
			style: 'currency',
			currency: 'EUR',
		});
	}

	disconnectedCallback() {
		// Cleanup
		if (this.observer) {
			this.observer.disconnect();
		}
		if (this.footerObserver) {
			this.footerObserver.disconnect();
		}
		if (this.stickyBar) {
			this.stickyBar.remove();
		}
	}
}

// Register the custom element
window.customElements.get('bundle-form') || window.customElements.define('bundle-form', BundleForm);
